/**
 * @file app/tauri/payment.js
 *
 * Novo payment-terminal bridge for the Tauri (desktop) build. Faithful port of
 * the `processCreditCard` (Wallee) / `processSzepCard` (SZÉP/SCA) /
 * `processNovoEndDay` / `processNovoEndDaySca` / `processNovoConnectionTest`
 * handlers from Happy-POS.
 *
 * - HMAC-SHA256(body, secret) → Base64 → `X-Request-Hash` header (crypto-js).
 * - POST https://{ip}:{port}/perform_action via the Tauri HTTP plugin with
 *   `danger.acceptInvalidCerts` (the terminal serves a local self-signed cert).
 *
 * The shared secret is sourced from `integrationsConfig` (env / per-restaurant
 * override), never inlined here.
 */

import CryptoJS from "crypto-js";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { fetch as tauriFetch } from "@tauri-apps/plugin-http";
import { getNovoTerminalSecret, NOVO_MERCHANT_REFERENCE } from "./integrationsConfig";

/**
 * Load the Novo terminal configuration the user entered on the settings screen
 * (`NovoTerminalSettingsScreen` → AsyncStorage `novoSettings`). This is the
 * authoritative source for the IP / port / timeout AND the HMAC **secret**.
 * @returns {Promise<{ipAddress?:string, portNumber?:string|number, timeout?:string|number, secret?:string}>}
 */
async function loadSavedTerminalConfig() {
  try {
    const raw = await AsyncStorage.getItem("novoSettings");
    if (!raw) return {};
    const s = JSON.parse(raw) || {};
    return {
      ipAddress: s.novoIpAddress || undefined,
      portNumber: s.novoPortNumber || undefined,
      timeout: s.novoTimeout || undefined,
      secret: s.novoSecret || undefined,
    };
  } catch (_e) {
    return {};
  }
}

/** Build the action payload for a given channel. */
function buildRawObject(channel, paymentData) {
  const amount = (Number(paymentData.amount) * 100).toString();
  const invoiceReference = "#" + paymentData.orderCode;
  const timeout = paymentData?.terminalConfig?.timeout;

  switch (channel) {
    case "processCreditCard": // Wallee credit-card purchase
      return {
        action: "npipe.action.purchase",
        pttpType: "Wallee",
        actionParams: {
          amount,
          amountGratuity: 0,
          timeout,
          currency: "HUF",
          merchantReference: NOVO_MERCHANT_REFERENCE,
          invoiceReference,
        },
      };
    case "processSzepCard": // SZÉP benefit card (SCA) purchase, with printout
      return {
        action: "npipe.action.purchase",
        pttpType: "Sca",
        actionParams: {
          printout: 1,
          amount,
          amountGratuity: 0,
          timeout,
          currency: "HUF",
          merchantReference: NOVO_MERCHANT_REFERENCE,
          invoiceReference,
        },
      };
    case "processNovoEndDay":
      return { action: "npipe.action.endofday", pttpType: "Wallee" };
    case "processNovoEndDaySca":
      return { action: "npipe.action.endofday", pttpType: "Sca", actionParams: { printout: 1 } };
    case "processNovoConnectionTest":
      return { action: "npipe.action.testconnect", pttpType: "Wallee" };
    default:
      return null;
  }
}

/**
 * Perform a Novo terminal action.
 * @param {string} channel one of the process* channel names
 * @param {object} paymentData { amount, orderCode, terminalConfig:{ipAddress,portNumber,timeout,secret} }
 * @returns {Promise<{success:boolean, message:*}>}
 */
export async function processPayment(channel, paymentData = {}) {
  // Settings-screen values are the base; an explicit per-call terminalConfig
  // (if any) overrides them field-by-field.
  const saved = await loadSavedTerminalConfig();
  const cfg = { ...saved, ...(paymentData.terminalConfig || {}) };
  const { ipAddress, portNumber, timeout } = cfg;
  // Priority: settings-screen / per-call secret → env → built-in fallback.
  const secret = getNovoTerminalSecret(cfg.secret);

  const rawObject = buildRawObject(channel, paymentData);
  if (!rawObject) return { success: false, message: `unknown payment channel ${channel}` };
  if (!ipAddress || !portNumber) {
    return { success: false, message: "terminal not configured (ipAddress/portNumber)" };
  }

  const raw = JSON.stringify(rawObject);
  const signHex = CryptoJS.enc.Base64.stringify(CryptoJS.HmacSHA256(raw, secret));

  const url = `https://${ipAddress}:${portNumber}/perform_action`;

  try {
    const response = await tauriFetch(url, {
      method: "POST",
      headers: {
        "X-Request-Hash": signHex.toString(),
        "Content-Type": "application/json; charset=UTF-8",
      },
      body: raw,
      connectTimeout: Number(timeout) || 60000,
      // The terminal uses a local self-signed certificate.
      danger: { acceptInvalidCerts: true, acceptInvalidHostnames: true },
    });

    let data;
    const text = await response.text();
    try {
      data = JSON.parse(text);
    } catch (_e) {
      data = text;
    }
    return { success: response.ok, message: data };
  } catch (error) {
    return { success: false, message: error && error.message ? error.message : String(error) };
  }
}

export default processPayment;
